import React from "react";
import AuthAdminUserModel from '../../code/models/AuthAdminUserModel';
import Toaster from "../Toaster";
import EmailTemplate from "../../code/models/EmailTemplate";
import ApiEmailTemplates from "../../code/api/ApiEmailTemplates";
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from "../CclDataTable";
import ButtonConfirm, { ButtonConfirmSize } from "../ButtonConfirm";
import { Badge, CloseButton, Spinner } from "reactstrap";
import EmailEditor from "../emaileditor/EmailEditor";
import Api_Config from "../../code/api/ApiConfig";
import CustomerDataTable from "../CustomerDataTable";
import Customer from "../../code/models/Customer";
import ApiCustomer from "../../code/api/ApiCustomer";
import "../../css/adminemailtemplates.css";

interface IAdminSendBatchEmail {
    adminUser: AuthAdminUserModel;
}

export default function AdminSendBatchEmail({ adminUser }: IAdminSendBatchEmail) {
    const [toastMessage, setToastMessage] = React.useState<string>("");
    const [templates, setTemplates] = React.useState<EmailTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = React.useState<EmailTemplate | null>(null);
    const [sending, setSending] = React.useState<boolean>(false);
    const [targetAll, setTargetAll] = React.useState<boolean>(true);
    const [fleet, setFleet] = React.useState<string>("");
    const [seat, setSeat] = React.useState<string>("");
    const [base, setBase] = React.useState<string>("");
    const [filteredData, setFilteredData] = React.useState<Customer[] | null>(null);

    const [sendMessage, setSendMessage] = React.useState<string>("Send this email template to all active Customers?");

    React.useEffect(() => {
        loadTemplates();
    }, []);

    React.useEffect(() => {
        loadCustomerList();
    }, [fleet, seat, base]);

    React.useEffect(() => {
        if (targetAll) {
            setFleet("");
            setSeat("");
            setBase("");
        }
    }, [targetAll]);

    React.useEffect(() => {
        if (targetAll) {
            setSendMessage("Send this email template to all active Customers?");
        } else {
            setSendMessage(`Send this email template to Customers for Fleet: ${fleet}, Seat: ${seat}, Base: ${base}?`);
        }
    }, [fleet, seat, base]);

    async function loadCustomerList() {
        ApiCustomer.getCustomersByFleetSeatBase(fleet, seat, base).then((custs) => {
            setFilteredData(custs);
        });
    }

    async function loadTemplates() {
        setSelectedTemplate(null);
        ApiEmailTemplates.getEmailTemplates().then((temps) => {
            setTemplates(temps);
        });
    }

    function rowSelected(rowData: EmailTemplate): void {
        setSelectedTemplate(rowData);
    }

    const bplColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "name",
            title: "Templates",
            visible: true,
            type: CclDataTableColumnType.Data
        }
    ];

    function emailCustomersOnConfirm(isConfirmed: boolean): void {        
        if (!selectedTemplate || !isConfirmed) return;

        if (targetAll) {
            setSending(true);
            ApiEmailTemplates.sendEmailActiveCustomers(selectedTemplate?.emailTemplateId).then((result) => {
                if (result) {
                    setToastMessage("Email request has been submitted for all active customers.");
                } else {
                    setToastMessage("Request Failed.");
                }
            }).finally(() => { setSending(false); });
        } else {
            var customerIds = filteredData?.map((cust) => cust.customerId);

            ApiEmailTemplates.sendEmailToCustomerList(selectedTemplate.emailTemplateId, customerIds || []).then((result) => {
                if (result) {
                    setToastMessage("Email request has been submitted to the custom list.");
                } else {
                    setToastMessage("Request Failed.");
                }
            }).finally(() => { setSending(false); });
        }
    }

    function buildOption(id: string, val: string): JSX.Element {
        return (
            <option key={id + val} value={val}>{val}</option>
        )
    };

    function buildSelect(id: string, label: string, value: string, items: string[] = [], onChange: (e: any) => any) {
        var options: JSX.Element[] = [];
        if (items && items.length > 0) {
            options = items.map<JSX.Element>((o) => buildOption(id, o));
        }

        return (
            <React.Fragment>
                <label htmlFor={id} className="form-label emailtemplates-selectlabel">{label}</label>
                <select className="form-select emailtemplates-select" id={id} defaultValue={value} onChange={onChange}>
                    <option key={"00000000any"} value={""}>Any</option>
                    {options}
                </select>
            </React.Fragment>
        )
    }

    function buildFilterDropDowns(): React.ReactNode {
        return (
            <React.Fragment>
                {buildSelect("selectFleet", "Fleet", fleet,
                    Api_Config.getSelectedCompany().aircraft, (e) => {
                        setFleet(e.target.value);
                    })}
                {buildSelect("selectSeat", "Seat", seat,
                    Api_Config.getSelectedCompany().positions, (e) => {
                        setSeat(e.target.value);
                    })}
                {buildSelect("selectBase", "Base", base,
                    Api_Config.getSelectedCompany().bases, (e) => {
                        setBase(e.target.value);
                    })}
            </React.Fragment>
        )
    }

    function closeTemplateClick(): void {
       setSelectedTemplate(null);
    }

    return (
        <React.Fragment>
            <div className="emmailtemplates-main">
                <div className="emailtemplates-left">
                    <div className="emmailtemplates-table-wrapper">
                        <CclDataTable
                            name={"profilesTable"}
                            tableData={templates || []}
                            columnDefinitions={bplColumnDefs}
                            onRowSelect={rowSelected}
                            rowSelect={true}
                            onButtonClick={null} />
                    </div>
                </div>
                <div className="emailtemplates-right" key={"et-edit-" + (selectedTemplate?.emailTemplateId || "a")}>
                    <div className="row emailtemplates-editortop">
                        <div className="col-md-3">
                            <label htmlFor="templateSubject" className="form-label">Subject</label>
                            <input className="form-control" disabled={!selectedTemplate} readOnly={true}
                                id="templateSubject" defaultValue={selectedTemplate?.subject} autoComplete={"off"}
                                onChange={(e) => {
                                    if (!!selectedTemplate) {
                                        selectedTemplate.subject = e.target.value;
                                    }
                                }}></input>
                        </div>
                        {!!selectedTemplate && <div className="col-md-1 emailtemplates-closebutton">
                            <CloseButton onClick={closeTemplateClick} />
                        </div>}
                        <div className="col-md-2 emailtemplates-optionbuttons">
                            <div className="btn-group" role="group" aria-label="Basic example" key={"batchemailtrgt" + targetAll}>
                                <button type="button" className={"btn btn-sm" + (targetAll ? " btn-primary active" : " btn-outline-primary")} onClick={() => setTargetAll(true)}>All Active</button>
                                <button type="button" className={"btn btn-sm" + (!targetAll ? " btn-primary active" : " btn-outline-primary")} onClick={() => setTargetAll(false)}>Custom</button>
                            </div>
                        </div>
                        {!targetAll && <div className="emailtemplates-target">
                            {buildFilterDropDowns()}
                        </div>}
                        <div className="col-md-3 emailtemplates-send">
                            {!sending && <ButtonConfirm buttonSize={ButtonConfirmSize.Small} buttonText={`Email ${targetAll ? "Active" : "Custom"} Customers`} confirmTitle="Email Customers"
                                confirmMessage={sendMessage} enabled={!!selectedTemplate} cssClass="" onConfirm={emailCustomersOnConfirm} />}
                            {sending && <Badge color="info" className="fs-5 emailtemplates-badge"><Spinner className="emailtemplates-spinner " size={"sm"}>Loading...</Spinner><span>{' '}Submitting</span></Badge>}
                        </div>
                    </div>
                    {!!selectedTemplate && <div className="row emailtemplates-editormain">
                        <EmailEditor
                            initialValue={selectedTemplate.template}
                            onChange={() => { }}
                            previewMode={true}
                            templateTags={undefined}
                        />
                    </div>}
                    {!selectedTemplate && <div className="row emailtemplates-table">
                        <CustomerDataTable
                            tableData={filteredData}
                            currentCustomerId={0}
                            customerSelectedCallback={() => { }}
                            customerActionCallback={() => { }} />
                    </div>}
                </div>
            </div>
            <Toaster title="Email Templates" subtitle="Email" message={toastMessage} key={"emailtemplatetoast"} />
        </React.Fragment>
    )
}
